import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Upload, User as UserIcon, ExternalLink, Palette, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { SimsConfirmDialog } from '../components/SimsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Obituary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [obituary, setObituary] = useState({
    content: '',
    photo_url: ''
  });

  useEffect(() => {
    loadObituary();
  }, []);

  const loadObituary = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/obituary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setObituary({
        content: response.data.content || '',
        photo_url: response.data.photo_url || ''
      });
    } catch (error) {
      console.error('Failed to load obituary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/obituary`,
        { content: obituary.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Obituary saved successfully');
    } catch (error) {
      console.error('Failed to save obituary:', error);
      toast.error('Failed to save obituary');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const token = localStorage.getItem('session_token');
    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/obituary/photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setObituary({ ...obituary, photo_url: response.data.photo_url });
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem('session_token');

    try {
      await axios.delete(`${BACKEND_URL}/api/obituary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Obituary deleted');
      setObituary({ content: '', photo_url: '' });
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete obituary:', error);
      toast.error('Failed to delete obituary');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B4C7E] border-r-transparent mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]" data-testid="obituary-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent truncate">Obituary</h1>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full hover:from-[#1E3A63] hover:to-[#1E3A63] text-xs sm:text-sm px-3 sm:px-4"
                data-testid="save-obituary-btn"
              >
                <Save className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save'}</span>
              </Button>
              {(obituary.content || obituary.photo_url) && (
                <Button
                  onClick={() => setDeleteDialogOpen(true)}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full"
                  data-testid="delete-obituary-btn"
                >
                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-8">
        <Card className="p-8 rounded-2xl border border-[#EBF0F5] bg-white/90 backdrop-blur-md shadow-lg">
          <div className="mb-8">
            <h2 className="text-2xl font-medium text-gray-800 mb-2">Pre-Write Your Obituary</h2>
            <p className="text-gray-600">
              Take control of how you&apos;ll be remembered. Write your own obituary to ensure your life story is told the way you want it.
            </p>
          </div>

          {/* Photo Upload */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">Your Photo</label>
            <div className="flex items-center gap-6">
              <div className="relative">
                {obituary.photo_url ? (
                  <img 
                    src={obituary.photo_url} 
                    alt="Obituary" 
                    className="w-32 h-32 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 bg-[#EBF0F5] rounded-lg flex items-center justify-center">
                    <UserIcon className="h-16 w-16 text-[#3A5C8F]" strokeWidth={1.5} />
                  </div>
                )}
                <label 
                  htmlFor="photo-upload" 
                  className="absolute bottom-2 right-2 w-10 h-10 bg-[#2B4C7E] text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-[#1E3A63] transition-colors shadow-lg"
                  title="Upload photo"
                >
                  {uploadingPhoto ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">
                  Upload a professional or favorite photo that you&apos;d like included in your obituary. This will be shown to beneficiaries.
                </p>
              </div>
            </div>
          </div>

          {/* Obituary Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Obituary Content</label>
            <Textarea
              placeholder="Example:

[Your Name], age [age], of [city, state], passed away peacefully on [date].

[Your Name] was born on [date] in [location] to [parents' names]. They graduated from [school] in [year] and went on to [career/life achievements].

[Your Name] is survived by [list family members]. They were preceded in death by [names].

[Your Name] will be remembered for their [qualities, hobbies, passions]. They loved [specific interests] and always [characteristic actions].

In lieu of flowers, the family requests donations be made to [charity/organization].

A [type of service] will be held on [date] at [location]."
              value={obituary.content}
              onChange={(e) => setObituary({ ...obituary, content: e.target.value })}
              rows={20}
              className="font-serif"
              data-testid="obituary-textarea"
            />
            <p className="text-xs text-gray-500 mt-2">
              Write in third person. Leave dates blank with [date] or [age] placeholders.
            </p>
          </div>
        </Card>

        {/* Guidelines Card */}
        <Card className="mt-8 p-6 rounded-2xl border border-[#EBF0F5] bg-gradient-to-br from-[#EBF0F5] to-white">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Writing Tips</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Include:</strong> Birth details, education, career, family, hobbies, and personality traits</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Tone:</strong> Celebratory and positive, focusing on life achievements and happy memories</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Length:</strong> Typically 200-500 words, but write what feels right for your story</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Format:</strong> Write in third person (he/she/they) as if someone else is telling your story</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Review:</strong> Update this periodically as your life evolves</span>
            </li>
          </ul>
        </Card>

        {/* Canva Design Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="mt-8 p-6 rounded-2xl border-2 border-[#D7E0EA] bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5] shadow-lg overflow-hidden relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#B9C7D6]/20 to-[#DCC39A]/20 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-[#B9C7D6]/20 to-[#B9C7D6]/20 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#2B4C7E] via-[#C8A97E] to-[#2B4C7E] rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Palette className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">Design Your Obituary with Canva</h3>
                  <p className="text-sm text-gray-600">
                    Create a beautiful, professionally designed obituary using Canva&apos;s free templates
                  </p>
                </div>
              </div>
              
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 mb-4">
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EBF0F5] text-[#2B4C7E] flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Hundreds of free obituary templates</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#F2E9D9] text-[#B3956B] flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Easy drag-and-drop editor</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#EBF0F5] text-[#2B4C7E] flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Add photos, graphics, and custom fonts</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">✓</span>
                    <span>Download as PDF or share online</span>
                  </li>
                </ul>
              </div>
              
              <a 
                href="https://www.canva.com/search/templates?q=obituary" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#2B4C7E] text-white rounded-full py-6 hover:opacity-90 shadow-lg group">
                  <Palette className="h-5 w-5 mr-2" />
                  Open Canva Obituary Templates
                  <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                Free to use • No account required to browse
              </p>
            </div>
          </Card>
        </motion.div>
      </main>

      {/* Delete Confirmation Dialog */}
      <SimsConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Obituary?"
        message="Are you sure you want to delete your obituary? This will remove all content and the photo you've uploaded."
        confirmText="Yes, Delete"
        cancelText="Keep It"
        variant="error"
      />
    </div>
  );
}
